import { SoundManager, SoundType } from '../audio/SoundManager';
import { getHelpMenu } from './HelpMenu';
import { getMobileControlsSettings } from './MobileControlsSettings';
import { isTouchDevice } from '../utils/MobileDetection';

/**
 * Event handlers for EscMenu
 */
export interface EscMenuEvents {
  onResume: () => void;
  onLeave: () => void;
  onEyeProtectionChange?: (enabled: boolean) => void;
}

/** localStorage key for eye protection setting */
const STORAGE_KEY_EYE_PROTECTION = 'wooorms_eye_protection';

/**
 * Get eye protection setting from localStorage
 */
export function getEyeProtection(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_EYE_PROTECTION) === 'true';
  } catch {
    return false;
  }
}

/**
 * Save eye protection setting to localStorage
 */
function setEyeProtection(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_EYE_PROTECTION, String(enabled));
  } catch {
    // localStorage not available
  }
}

/**
 * EscMenu - In-game pause/leave menu
 *
 * Features:
 * - Shows when ESC is pressed during gameplay
 * - Resume option to continue playing
 * - Leave option that triggers suicide (player dies, drops points)
 * - Semi-transparent overlay
 * - Keyboard navigation (ESC to close)
 */
export class EscMenu {
  /** Container element */
  private container: HTMLDivElement;

  /** Content wrapper */
  private content: HTMLDivElement;

  /** Event handlers */
  private events: EscMenuEvents;

  /** Whether UI is visible */
  private visible: boolean = false;

  /** Keyboard listener reference for cleanup */
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;

  constructor(events: EscMenuEvents) {
    this.events = events;

    // Inject styles
    this.injectStyles();

    // Create container with semi-transparent overlay
    this.container = document.createElement('div');
    this.container.id = 'esc-menu';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      justify-content: center;
      align-items: center;
      background: rgba(10, 10, 20, 0.85);
      backdrop-filter: blur(4px);
      z-index: 900;
      font-family: 'Segoe UI', Arial, sans-serif;
      opacity: 0;
      transition: opacity 0.2s ease-out;
    `;

    // Content wrapper
    this.content = document.createElement('div');
    this.content.style.cssText = `
      text-align: center;
      max-width: 400px;
      padding: 40px 50px;
      background: linear-gradient(145deg, rgba(40, 40, 70, 0.95), rgba(25, 25, 45, 0.98));
      border-radius: 20px;
      border: 2px solid rgba(78, 205, 196, 0.3);
      box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(78, 205, 196, 0.1);
      transform: scale(0.9) translateY(-20px);
      transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    `;

    // Pause icon
    const pauseIcon = document.createElement('div');
    pauseIcon.innerHTML = '⏸️';
    pauseIcon.style.cssText = `
      font-size: 56px;
      margin-bottom: 15px;
      filter: drop-shadow(0 4px 15px rgba(78, 205, 196, 0.4));
    `;
    this.content.appendChild(pauseIcon);

    // Title
    const title = document.createElement('h2');
    title.textContent = 'PAUSED';
    title.style.cssText = `
      font-size: 32px;
      font-weight: 800;
      letter-spacing: 4px;
      margin: 0 0 8px 0;
      color: #4ecdc4;
      text-shadow: 0 2px 10px rgba(78, 205, 196, 0.5);
    `;
    this.content.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('p');
    subtitle.textContent = 'Press ESC or P to resume';
    subtitle.style.cssText = `
      color: #888;
      margin: 0 0 30px 0;
      font-size: 14px;
    `;
    this.content.appendChild(subtitle);

    // Buttons container
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 14px;
    `;

    // Resume button
    const resumeButton = document.createElement('button');
    resumeButton.innerHTML = '<span style="margin-right: 10px;">▶️</span>Resume Game';
    resumeButton.style.cssText = `
      width: 100%;
      padding: 16px 28px;
      font-size: 17px;
      font-weight: 700;
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #4ecdc4 0%, #44b8b0 50%, #3aa89e 100%);
      color: #fff;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 15px rgba(78, 205, 196, 0.3);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    `;
    resumeButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this.events.onResume();
    });
    resumeButton.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      resumeButton.style.transform = 'translateY(-3px) scale(1.02)';
      resumeButton.style.boxShadow = '0 8px 25px rgba(78, 205, 196, 0.5)';
    });
    resumeButton.addEventListener('mouseout', () => {
      resumeButton.style.transform = 'translateY(0) scale(1)';
      resumeButton.style.boxShadow = '0 4px 15px rgba(78, 205, 196, 0.3)';
    });
    buttonsContainer.appendChild(resumeButton);

    // Settings section (Sound and Eye Protection)
    const settingsSection = document.createElement('div');
    settingsSection.style.cssText = `
      display: flex;
      gap: 10px;
      justify-content: center;
      margin: 8px 0;
    `;

    // Sound toggle
    const soundManager = SoundManager.getInstance();
    const soundToggle = this.createToggleButton(
      soundManager.muted ? '🔇' : '🔊',
      soundManager.muted ? 'Sound Off' : 'Sound On',
      !soundManager.muted,
      (enabled) => {
        soundManager.muted = !enabled;
        soundToggle.querySelector('.toggle-icon')!.textContent = enabled ? '🔊' : '🔇';
        soundToggle.querySelector('.toggle-label')!.textContent = enabled ? 'Sound On' : 'Sound Off';
      }
    );
    settingsSection.appendChild(soundToggle);

    // Eye Protection toggle
    let eyeProtectionEnabled = getEyeProtection();
    const eyeToggle = this.createToggleButton(
      '👁️',
      eyeProtectionEnabled ? 'Eye Shield' : 'Normal View',
      eyeProtectionEnabled,
      (enabled) => {
        eyeProtectionEnabled = enabled;
        setEyeProtection(enabled);
        eyeToggle.querySelector('.toggle-label')!.textContent = enabled ? 'Eye Shield' : 'Normal View';
        if (this.events.onEyeProtectionChange) {
          this.events.onEyeProtectionChange(enabled);
        }
      }
    );
    settingsSection.appendChild(eyeToggle);

    buttonsContainer.appendChild(settingsSection);

    // Help button
    const helpButton = document.createElement('button');
    helpButton.innerHTML = '<span style="margin-right: 10px;">📖</span>How to Play';
    helpButton.style.cssText = `
      width: 100%;
      padding: 14px 24px;
      font-size: 15px;
      font-weight: 600;
      border: 2px solid rgba(78, 205, 196, 0.4);
      border-radius: 10px;
      background: linear-gradient(145deg, rgba(78, 205, 196, 0.1), rgba(78, 205, 196, 0.05));
      color: #7dd8d1;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    helpButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      // Hide the pause menu temporarily while showing help
      this.hide();
      getHelpMenu().show(() => {
        // When help menu closes, show the pause menu again
        this.show();
      });
    });
    helpButton.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      helpButton.style.borderColor = 'rgba(78, 205, 196, 0.7)';
      helpButton.style.background = 'linear-gradient(145deg, rgba(78, 205, 196, 0.2), rgba(78, 205, 196, 0.1))';
      helpButton.style.color = '#9de8e2';
      helpButton.style.transform = 'translateY(-2px)';
    });
    helpButton.addEventListener('mouseout', () => {
      helpButton.style.borderColor = 'rgba(78, 205, 196, 0.4)';
      helpButton.style.background = 'linear-gradient(145deg, rgba(78, 205, 196, 0.1), rgba(78, 205, 196, 0.05))';
      helpButton.style.color = '#7dd8d1';
      helpButton.style.transform = 'translateY(0)';
    });
    buttonsContainer.appendChild(helpButton);

    // Mobile Controls button (only on touch devices)
    if (isTouchDevice()) {
      const mobileControlsButton = document.createElement('button');
      mobileControlsButton.innerHTML = '<span style="margin-right: 10px;">&#x1F3AE;</span>Mobile Controls';
      mobileControlsButton.style.cssText = `
        width: 100%;
        padding: 14px 24px;
        font-size: 15px;
        font-weight: 600;
        border: 2px solid rgba(167, 139, 250, 0.4);
        border-radius: 10px;
        background: linear-gradient(145deg, rgba(167, 139, 250, 0.1), rgba(167, 139, 250, 0.05));
        color: #c4b5fd;
        cursor: pointer;
        transition: all 0.2s ease;
      `;
      mobileControlsButton.addEventListener('click', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        // Hide the pause menu temporarily while showing mobile controls settings
        this.hide();
        const mobileSettings = getMobileControlsSettings({
          onClose: () => {
            // When mobile controls settings closes, show the pause menu again
            this.show();
          },
        });
        mobileSettings.show();
      });
      mobileControlsButton.addEventListener('mouseover', () => {
        SoundManager.getInstance().play(SoundType.UI_HOVER);
        mobileControlsButton.style.borderColor = 'rgba(167, 139, 250, 0.7)';
        mobileControlsButton.style.background = 'linear-gradient(145deg, rgba(167, 139, 250, 0.2), rgba(167, 139, 250, 0.1))';
        mobileControlsButton.style.color = '#ddd6fe';
        mobileControlsButton.style.transform = 'translateY(-2px)';
      });
      mobileControlsButton.addEventListener('mouseout', () => {
        mobileControlsButton.style.borderColor = 'rgba(167, 139, 250, 0.4)';
        mobileControlsButton.style.background = 'linear-gradient(145deg, rgba(167, 139, 250, 0.1), rgba(167, 139, 250, 0.05))';
        mobileControlsButton.style.color = '#c4b5fd';
        mobileControlsButton.style.transform = 'translateY(0)';
      });
      buttonsContainer.appendChild(mobileControlsButton);
    }

    // Leave button
    const leaveButton = document.createElement('button');
    leaveButton.innerHTML = '<span style="margin-right: 10px;">🚪</span>Leave Game';
    leaveButton.style.cssText = `
      width: 100%;
      padding: 14px 24px;
      font-size: 15px;
      font-weight: 600;
      border: 2px solid rgba(255, 107, 107, 0.4);
      border-radius: 10px;
      background: linear-gradient(145deg, rgba(255, 107, 107, 0.1), rgba(255, 107, 107, 0.05));
      color: #ff9999;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    leaveButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this.events.onLeave();
    });
    leaveButton.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      leaveButton.style.borderColor = 'rgba(255, 107, 107, 0.7)';
      leaveButton.style.background = 'linear-gradient(145deg, rgba(255, 107, 107, 0.2), rgba(255, 107, 107, 0.1))';
      leaveButton.style.color = '#ffbbbb';
      leaveButton.style.transform = 'translateY(-2px)';
    });
    leaveButton.addEventListener('mouseout', () => {
      leaveButton.style.borderColor = 'rgba(255, 107, 107, 0.4)';
      leaveButton.style.background = 'linear-gradient(145deg, rgba(255, 107, 107, 0.1), rgba(255, 107, 107, 0.05))';
      leaveButton.style.color = '#ff9999';
      leaveButton.style.transform = 'translateY(0)';
    });
    buttonsContainer.appendChild(leaveButton);

    // Warning text
    const warning = document.createElement('p');
    warning.innerHTML = '⚠️ <span style="color:#ff9999;">Leaving will end your game!</span><br>Your points will be dropped for others.';
    warning.style.cssText = `
      color: #777;
      margin: 20px 0 0 0;
      font-size: 12px;
      line-height: 1.5;
    `;
    buttonsContainer.appendChild(warning);

    this.content.appendChild(buttonsContainer);
    this.container.appendChild(this.content);
    document.body.appendChild(this.container);
  }

  /**
   * Inject CSS keyframe animations
   */
  private injectStyles(): void {
    if (document.getElementById('esc-menu-styles')) return;

    const style = document.createElement('style');
    style.id = 'esc-menu-styles';
    style.textContent = `
      @keyframes esc-menu-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Show the menu
   */
  show(): void {
    if (this.visible) return;

    this.visible = true;
    this.container.style.display = 'flex';

    // Trigger animations
    requestAnimationFrame(() => {
      this.container.style.opacity = '1';
      this.content.style.transform = 'scale(1) translateY(0)';
    });

    // Add keyboard listener to close on ESC
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.events.onResume();
      }
    };
    document.addEventListener('keydown', this.keydownListener);
  }

  /**
   * Hide the menu
   */
  hide(): void {
    if (!this.visible) return;

    this.visible = false;

    // Remove keyboard listener
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }

    // Animate out
    this.container.style.opacity = '0';
    this.content.style.transform = 'scale(0.9) translateY(-20px)';

    setTimeout(() => {
      this.container.style.display = 'none';
    }, 200);
  }

  /**
   * Toggle visibility
   */
  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Create a toggle button for settings
   */
  private createToggleButton(
    icon: string,
    label: string,
    initialState: boolean,
    onChange: (enabled: boolean) => void
  ): HTMLDivElement {
    let enabled = initialState;

    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 12px 16px;
      border-radius: 10px;
      background: ${enabled ? 'rgba(78, 205, 196, 0.15)' : 'rgba(100, 100, 120, 0.15)'};
      border: 2px solid ${enabled ? 'rgba(78, 205, 196, 0.4)' : 'rgba(100, 100, 120, 0.3)'};
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 90px;
    `;

    const iconEl = document.createElement('span');
    iconEl.className = 'toggle-icon';
    iconEl.textContent = icon;
    iconEl.style.cssText = `
      font-size: 24px;
    `;
    container.appendChild(iconEl);

    const labelEl = document.createElement('span');
    labelEl.className = 'toggle-label';
    labelEl.textContent = label;
    labelEl.style.cssText = `
      font-size: 11px;
      color: ${enabled ? '#4ecdc4' : '#888'};
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    `;
    container.appendChild(labelEl);

    const updateStyle = () => {
      container.style.background = enabled ? 'rgba(78, 205, 196, 0.15)' : 'rgba(100, 100, 120, 0.15)';
      container.style.borderColor = enabled ? 'rgba(78, 205, 196, 0.4)' : 'rgba(100, 100, 120, 0.3)';
      labelEl.style.color = enabled ? '#4ecdc4' : '#888';
    };

    container.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      enabled = !enabled;
      updateStyle();
      onChange(enabled);
    });

    container.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      container.style.transform = 'scale(1.05)';
    });

    container.addEventListener('mouseout', () => {
      container.style.transform = 'scale(1)';
    });

    return container;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
    }
    this.container.remove();
  }
}
